const WEEK_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export function getReasonClosedReasons(store) {
  return Object.values(store.reason_closed || {});
}

export function getReasonClosedId(store) {
  const reasons = getReasonClosedReasons(store);

  if (reasons.includes('holiday')) {
    return 'holiday';
  }
  if (reasons.includes('capacity')) {
    return 'capacity';
  }
  if (reasons.includes('currency')) {
    return 'currency';
  }

  return 'schedule';
}
function getDaySchedules(store, date) {
  const searchDayName = WEEK_DAYS[date.getDay()];
  const availability = (store.availabilities || []).find(
    (dayAvailability) => dayAvailability.day === searchDayName,
  );

  if (!availability || !availability.schedules || availability.schedules.length === 0) {
    return [];
  }

  return availability.schedules;
}

function timeToSeconds(time) {
  const [hours, minutes, seconds = '0'] = time.split(':');
  return Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
}

function dateToSeconds(date) {
  return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

function setDateTime(date, scheduleTime) {
  const [hours, minutes, seconds = '0'] = scheduleTime.split(':');
  const updatedDate = new Date(date);
  updatedDate.setHours(Number(hours), Number(minutes), Number(seconds), 0);
  return updatedDate;
}

function secondsToTime(seconds) {
  const hours = Math.floor(seconds / 3600).toString().padStart(2, '0');
  const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
  const remainingSeconds = (seconds % 60).toString().padStart(2, '0');
  return `${hours}:${minutes}:${remainingSeconds}`;
}

function isSameDate(startDate, endDate) {
  return startDate.getFullYear() === endDate.getFullYear()
    && startDate.getMonth() === endDate.getMonth()
    && startDate.getDate() === endDate.getDate();
}

function getSameDayAdjustment(store, startDate, endDate) {
  const requestedStart = dateToSeconds(startDate);
  const requestedEnd = dateToSeconds(endDate);
  const schedules = getDaySchedules(store, startDate);

  const availableSchedule = schedules.find((schedule) => {
    const open = timeToSeconds(schedule.open_hour);
    const close = timeToSeconds(schedule.close_hour);
    const adjustedStart = Math.max(requestedStart, open);
    const adjustedEnd = Math.min(requestedEnd, close);

    return adjustedStart < adjustedEnd;
  });

  if (availableSchedule) {
    const open = timeToSeconds(availableSchedule.open_hour);
    const close = timeToSeconds(availableSchedule.close_hour);
    const adjustedStart = Math.max(requestedStart, open);
    const adjustedEnd = Math.min(requestedEnd, close);

    return {
      startDate: setDateTime(
        startDate,
        adjustedStart === open ? availableSchedule.open_hour : secondsToTime(adjustedStart),
      ),
      endDate: setDateTime(
        endDate,
        adjustedEnd === close ? availableSchedule.close_hour : secondsToTime(adjustedEnd),
      ),
    };
  }

  return null;
}

function getDifferentDaysAdjustment(store, startDate, endDate) {
  const startSchedules = getDaySchedules(store, startDate);
  const endSchedules = getDaySchedules(store, endDate);

  if (startSchedules.length === 0 || endSchedules.length === 0) {
    return null;
  }

  const startSeconds = dateToSeconds(startDate);
  const endSeconds = dateToSeconds(endDate);
  const startSchedule = startSchedules.find(
    (schedule) => startSeconds <= timeToSeconds(schedule.close_hour),
  );
  const endSchedule = endSchedules.find(
    (schedule) => endSeconds >= timeToSeconds(schedule.open_hour),
  );

  if (!startSchedule || !endSchedule) {
    return null;
  }

  const adjustedStart = Math.max(startSeconds, timeToSeconds(startSchedule.open_hour));
  const adjustedEnd = Math.min(endSeconds, timeToSeconds(endSchedule.close_hour));

  return {
    startDate: setDateTime(
      startDate,
      adjustedStart === timeToSeconds(startSchedule.open_hour)
        ? startSchedule.open_hour
        : secondsToTime(adjustedStart),
    ),
    endDate: setDateTime(
      endDate,
      adjustedEnd === timeToSeconds(endSchedule.close_hour)
        ? endSchedule.close_hour
        : secondsToTime(adjustedEnd),
    ),
  };
}

export function formatScheduleHour(date) {
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');

  if (minutes === '00') {
    return `${hours}`;
  }

  return `${hours}:${minutes}`;
}

export function getScheduleBookingAdjustment(store, searchParams) {
  if (!store || !searchParams || !searchParams.input_start_date || !searchParams.input_end_date) {
    return null;
  }

  if (store.isAvailable || !getReasonClosedReasons(store).includes('schedule')) {
    return null;
  }

  const startDate = new Date(searchParams.input_start_date);
  const endDate = new Date(searchParams.input_end_date);
  const adjustment = isSameDate(startDate, endDate)
    ? getSameDayAdjustment(store, startDate, endDate)
    : getDifferentDaysAdjustment(store, startDate, endDate);

  if (!adjustment || adjustment.endDate <= adjustment.startDate) {
    return null;
  }

  return {
    ...adjustment,
    startHour: formatScheduleHour(adjustment.startDate),
    endHour: formatScheduleHour(adjustment.endDate),
  };
}

export function getCurrencyBookingAdjustment(store, currentCurrency = 'USD') {
  if (!store || store.isAvailable || !getReasonClosedReasons(store).includes('currency')) {
    return null;
  }

  const currencies = store.available_currencies || [];
  const currency = currencies.includes('USD') || currencies.length === 0
    ? 'USD'
    : currencies[0];

  if (!currency || currency === currentCurrency) {
    return null;
  }

  return { currency };
}

export function getStoreBookingAdjustment(store, searchParams, currentCurrency = 'USD') {
  if (!store || store.isAvailable) {
    return null;
  }

  const reasons = getReasonClosedReasons(store);
  const hasBlockingReason = reasons.some(
    (reason) => !['schedule', 'currency'].includes(reason),
  );

  if (hasBlockingReason) {
    return null;
  }

  const scheduleAdjustment = getScheduleBookingAdjustment(store, searchParams);
  const currencyAdjustment = getCurrencyBookingAdjustment(store, currentCurrency);

  if (reasons.includes('schedule') && !scheduleAdjustment) {
    return null;
  }
  if (reasons.includes('currency') && !currencyAdjustment) {
    return null;
  }
  if (!scheduleAdjustment && !currencyAdjustment) {
    return null;
  }

  return {
    ...(scheduleAdjustment || {}),
    ...(currencyAdjustment || {}),
  };
}
